import { Modal, Text, Button, Group, Stack, Code } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle } from '@tabler/icons-react';
import { api } from '@/services/api';
import type { Environment } from '@shared/types';

interface DeleteEnvironmentModalProps {
  environment: Environment | null;
  opened: boolean;
  onClose: () => void;
}

export function DeleteEnvironmentModal({
  environment,
  opened,
  onClose,
}: DeleteEnvironmentModalProps) {
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteEnvironment(id),
    onSuccess: () => {
      // Invalidate environments list
      queryClient.invalidateQueries({ queryKey: ['environments'] });
      
      notifications.show({
        title: 'Environment deleted',
        message: `Environment "${environment?.name}" has been successfully deleted`,
        color: 'green',
      });
      
      onClose();
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Delete failed',
        message: error.message,
        color: 'red',
      });
    },
  });

  const handleDelete = () => {
    if (environment) {
      deleteMutation.mutate(environment.id);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Delete Environment"
      centered
    >
      <Stack gap="md">
        <Group>
          <IconAlertTriangle size={24} color="red" />
          <Text fw={500}>Are you sure you want to delete this environment?</Text>
        </Group>
        
        <Text size="sm" c="dimmed">
          This action cannot be undone. All resources associated with this environment will be
          deleted.
        </Text>
        
        {environment && (
          <Stack gap="xs">
            <Text size="sm" fw={500}>Environment:</Text>
            <Code block>{environment.name}</Code>
            <Text size="sm" c="dimmed">
              Namespace: {environment.namespace}
            </Text>
          </Stack>
        )}
        
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose} disabled={deleteMutation.isPending}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleDelete}
            loading={deleteMutation.isPending}
          >
            Delete
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
